/**
 * Analysis Page - Correlation charting for measurements, dosing, and events.
 *
 * Features:
 * - Sidebar for selecting data series (parameters, dosing, events)
 * - Time controls for date range selection
 * - URL-based state for shareable links
 * - View toggle: Stacked (individual charts) or Combined (normalized overlay)
 * - Responsive: drawer on mobile, fixed sidebar on desktop
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Title, Text, Drawer, ActionIcon, Group, Alert, Loader, Center, SegmentedControl, Tooltip, Switch, Menu, Select } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconMenu2, IconAlertCircle, IconLayoutRows, IconChartLine, IconLine, IconStar, IconStarFilled, IconDownload, IconPhoto, IconFileSpreadsheet } from '@tabler/icons-react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useTank } from '@/contexts/TankContext';
import { NoTankSelected } from '@/components/common/NoTankSelected';
import { useDateRangeParams } from '@/hooks/useDateRangeParams';
import { useAnalysisData, extractAvailableData } from '@/hooks/useAnalysisData';
import { useChartAnnotations } from '@/hooks/useChartAnnotations';
import { useTargetRanges } from '@/hooks/useTargetRanges';
import { AnalysisSidebar, AnalysisSelection } from '@/features/analysis/AnalysisSidebar';
import { AnalysisTimeControls } from '@/features/analysis/AnalysisTimeControls';
import { AnalysisStackedView } from '@/features/analysis/AnalysisStackedView';
import { AnalysisCombinedView } from '@/features/analysis/AnalysisCombinedView';
import { AnalysisEmptyState } from '@/features/analysis/AnalysisEmptyState';
import { AnalysisErrorBoundary } from '@/features/analysis/AnalysisErrorBoundary';
import { notifications } from '@mantine/notifications';
import { useAnalysisViews, useCreateAnalysisView, useUpdateAnalysisView, useDeleteAnalysisView, configsMatch } from '@/hooks/useAnalysisViews';
import { SaveViewModal } from '@/features/analysis/SaveViewModal';
import { useExportImage } from '@/hooks/useExportImage';
import { exportAnalysisExcel } from '@/features/analysis/utils/analysisExcelExport';
import { AddAnnotationModal } from '@/components/charts/AddAnnotationModal';
import { parseSmoothingParam } from '@/pages/analysisPageParams';
import { smoothingWindowToMs, SMOOTHING_OPTIONS, type SmoothingWindow } from '@/features/analysis/utils/smoothing';
import { ChartOptionsPopover } from '@/features/analysis/ChartOptionsPopover';
import type { ViewConfig, AnalysisView } from '@/types/analysisView';
import type { ChartAnnotation } from '@/types/chartAnnotation';

/** View mode type for chart display */
type ViewMode = 'stacked' | 'combined';

/** Parse selection from URL search params */
function parseSelectionFromParams(searchParams: URLSearchParams): AnalysisSelection {
  return {
    parameters: searchParams.getAll('params'),
    dosing: searchParams.getAll('dosing'),
    events: searchParams.getAll('events'),
  };
}

/** Write selection to URL search params (sorted for stable URLs) */
function writeSelectionToParams(
  prev: URLSearchParams,
  selection: AnalysisSelection
): URLSearchParams {
  // Remove existing selection params
  prev.delete('params');
  prev.delete('dosing');
  prev.delete('events');

  // Add sorted params for stable URLs
  [...selection.parameters].sort().forEach((p) => prev.append('params', p));
  [...selection.dosing].sort().forEach((d) => prev.append('dosing', d));
  [...selection.events].sort().forEach((e) => prev.append('events', e));

  return prev;
}

const SIDEBAR_WIDTH = 280;

export default function AnalysisPage() {
  const { selectedTank } = useTank();
  const isMobile = useIsMobile();
  const [sidebarOpened, { open: openSidebar, close: closeSidebar }] = useDisclosure(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-based date range state for shareable links
  const { dateRange, setDateRange, startDateString, endDateString } = useDateRangeParams();

  // View mode state from URL params (default: stacked)
  const viewMode: ViewMode = (searchParams.get('view') as ViewMode) || 'stacked';
  const setViewMode = useCallback(
    (mode: ViewMode) => {
      setSearchParams(
        (prev) => {
          if (mode === 'stacked') {
            // Remove view param when it's the default value
            prev.delete('view');
            prev.delete('normalized');
          } else {
            prev.set('view', mode);
          }
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Normalized mode for combined view from URL params (default: true)
  const normalized = searchParams.get('normalized') !== 'false';
  const setNormalized = useCallback(
    (value: boolean) => {
      setSearchParams(
        (prev) => {
          if (value) {
            // Remove param when true (default value)
            prev.delete('normalized');
          } else {
            prev.set('normalized', 'false');
          }
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Show event lines state from URL params (default: true)
  const showEventLines = searchParams.get('eventLines') !== 'false';
  const setShowEventLines = useCallback(
    (show: boolean) => {
      setSearchParams(
        (prev) => {
          if (!show) {
            prev.set('eventLines', 'false');
          } else {
            // Remove param when true (default value)
            prev.delete('eventLines');
          }
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Smoothing window from URL params (default: 'off'); applies to both views.
  const smoothing = parseSmoothingParam(searchParams);
  const setSmoothing = useCallback(
    (value: SmoothingWindow) => {
      setSearchParams(
        (prev) => {
          if (value === 'off') {
            // Remove param when off (default value)
            prev.delete('smoothing');
          } else {
            prev.set('smoothing', value);
          }
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // ---- Saved Views state ----
  const { data: savedViews = [] } = useAnalysisViews();
  const createView = useCreateAnalysisView();
  const updateView = useUpdateAnalysisView();
  const deleteView = useDeleteAnalysisView();
  const [saveModalOpened, { open: openSaveModal, close: closeSaveModal }] = useDisclosure(false);

  // Track which view was loaded (ephemeral — resets on navigation)
  const [loadedViewId, setLoadedViewId] = useState<string | null>(null);

  // Chart export
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const { exportElement, isExporting } = useExportImage();
  const [isExcelExporting, setIsExcelExporting] = useState(false);

  // Selection state derived from URL params
  const selection = useMemo(
    () => parseSelectionFromParams(searchParams),
    [searchParams]
  );

  // Update selection by writing to URL params
  const setSelection = useCallback(
    (newSelection: AnalysisSelection) => {
      setSearchParams(
        (prev) => writeSelectionToParams(prev, newSelection),
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Fetch analysis data from API
  const {
    data: analysisData,
    isLoading,
    error,
  } = useAnalysisData({
    tankId: selectedTank?.id,
    startDate: startDateString,
    endDate: endDateString,
  });

  // Tank target ranges, drawn as a green band on single-parameter charts
  const { data: targetRangesData } = useTargetRanges();

  // Fetch chart annotations for the selected date range
  const { data: annotations = [] } = useChartAnnotations(selectedTank?.id, {
    startDate: dateRange[0] || undefined,
    endDate: dateRange[1] || undefined,
  });

  // Annotation modal state
  const [annotationModalOpened, setAnnotationModalOpened] = useState(false);
  const [selectedAnnotation, setSelectedAnnotation] = useState<ChartAnnotation | null>(null);
  const [annotationChartContext, setAnnotationChartContext] = useState<{
    parameter: string;
    parameterLabel: string;
    sources?: string[];
    series?: string[];
  } | null>(null);

  // Handlers for annotation modal
  const handleAddNote = useCallback((
    parameter: string,
    parameterLabel: string,
    sources?: string[]
  ) => {
    // Use viewMode state directly instead of inferring from composite key format
    const isCombined = viewMode === 'combined';
    setAnnotationChartContext({
      parameter,
      parameterLabel,
      sources: isCombined ? undefined : sources,
      series: isCombined ? sources : undefined,
    });
    setSelectedAnnotation(null);
    setAnnotationModalOpened(true);
  }, [viewMode]);

  const handleEditAnnotation = useCallback((annotation: ChartAnnotation) => {
    // In combined view, pass current selected parameters as series for the picker
    const isCombined = viewMode === 'combined';
    setAnnotationChartContext({
      parameter: annotation.parameter || '',
      parameterLabel: isCombined ? 'Combined View' : (annotation.parameter || 'All Charts'),
      sources: undefined,
      series: isCombined ? selection.parameters : undefined,
    });
    setSelectedAnnotation(annotation);
    setAnnotationModalOpened(true);
  }, [viewMode, selection.parameters]);

  // Extract available data for sidebar from API response
  const availableData = useMemo(
    () => extractAvailableData(analysisData),
    [analysisData]
  );

  // Handle selection changes from sidebar
  const handleSelectionChange = useCallback(
    (newSelection: AnalysisSelection) => {
      setSelection(newSelection);
      // Close mobile drawer when selection changes
      if (isMobile) {
        closeSidebar();
      }
    },
    [isMobile, closeSidebar, setSelection]
  );

  // Build current config for comparison
  const currentConfig = useMemo((): ViewConfig => ({
    parameters: [...selection.parameters].sort(),
    dosing: [...selection.dosing].sort(),
    events: [...selection.events].sort(),
    viewMode,
    normalized,
    eventLines: showEventLines,
    smoothing,
  }), [selection, viewMode, normalized, showEventLines, smoothing]);

  // Find the active view: either the loaded view (if still matching) or any view that matches current config
  const activeView = useMemo((): AnalysisView | null => {
    // First check loaded view
    if (loadedViewId) {
      const loaded = savedViews.find((v) => v.id === loadedViewId);
      if (loaded) return loaded;
    }
    // Then check if any saved view matches current config exactly
    return savedViews.find((v) => configsMatch(v.config, currentConfig)) || null;
  }, [savedViews, loadedViewId, currentConfig]);

  // Determine star icon state
  const isExactMatch = activeView ? configsMatch(activeView.config, currentConfig) : false;
  const isModified = activeView !== null && !isExactMatch;
  // starState: 'empty' | 'saved' | 'modified'
  const starState = activeView === null ? 'empty' : isExactMatch ? 'saved' : 'modified';

  // ---- Saved Views handlers ----
  const handleLoadView = useCallback((view: AnalysisView) => {
    const c = view.config;
    setSearchParams((prev) => {
      // Clear existing selection params
      prev.delete('params');
      prev.delete('dosing');
      prev.delete('events');

      // Set selections from view config
      [...c.parameters].sort().forEach((p) => prev.append('params', p));
      [...c.dosing].sort().forEach((d) => prev.append('dosing', d));
      [...c.events].sort().forEach((e) => prev.append('events', e));

      // Set view mode
      if (c.viewMode === 'combined') {
        prev.set('view', 'combined');
      } else {
        prev.delete('view');
      }

      // Set normalized
      if (!c.normalized) {
        prev.set('normalized', 'false');
      } else {
        prev.delete('normalized');
      }

      // Set event lines (default is true, so only set param when false)
      if (c.eventLines === false) {
        prev.set('eventLines', 'false');
      } else {
        prev.delete('eventLines');
      }

      // Set smoothing (default is 'off', so only set param when not off)
      if (c.smoothing && c.smoothing !== 'off') {
        prev.set('smoothing', c.smoothing);
      } else {
        prev.delete('smoothing');
      }

      return prev;
    }, { replace: true });

    setLoadedViewId(view.id);
  }, [setSearchParams]);

  const handleSaveNew = useCallback((name: string) => {
    createView.mutate(
      { name, config: currentConfig },
      {
        onSuccess: (created) => {
          setLoadedViewId(created.id);
          notifications.show({ message: `View "${name}" saved`, color: 'teal' });
        },
        onError: () => {
          notifications.show({ message: 'Failed to save view', color: 'red' });
        },
      }
    );
  }, [createView, currentConfig]);

  const handleUpdateView = useCallback((viewId: string) => {
    updateView.mutate(
      { viewId, data: { config: currentConfig } },
      {
        onSuccess: () => {
          notifications.show({ message: 'View updated', color: 'teal' });
        },
        onError: () => {
          notifications.show({ message: 'Failed to save view', color: 'red' });
        },
      }
    );
  }, [updateView, currentConfig]);

  const handleRenameView = useCallback((viewId: string, newName: string) => {
    updateView.mutate({ viewId, data: { name: newName } }, {
      onError: () => {
        notifications.show({ message: 'Failed to rename view', color: 'red' });
      },
    });
  }, [updateView]);

  const handleDeleteView = useCallback((viewId: string) => {
    deleteView.mutate(viewId, {
      onSuccess: () => {
        if (loadedViewId === viewId) setLoadedViewId(null);
        notifications.show({ message: 'View deleted', color: 'gray' });
      },
      onError: () => {
        notifications.show({ message: 'Failed to delete view', color: 'red' });
      },
    });
  }, [deleteView, loadedViewId]);

  const handleExportImage = useCallback(async () => {
    const el = chartAreaRef.current;
    if (!el) return;
    // Temporarily expand scrollable area so html2canvas captures full content
    const original = { overflow: el.style.overflow, height: el.style.height, maxHeight: el.style.maxHeight };
    el.style.overflow = 'visible';
    el.style.height = 'auto';
    el.style.maxHeight = 'none';
    try {
      await exportElement(el, 'analysis', { brandingPadding: 32 });
      notifications.show({ title: 'Charts exported', message: 'Analysis saved as PNG', color: 'green', autoClose: 3000 });
    } catch {
      notifications.show({ title: 'Export failed', message: 'Could not export charts. Please try again.', color: 'red' });
    } finally {
      Object.assign(el.style, original);
    }
  }, [exportElement]);

  const handleExportExcel = useCallback(async () => {
    setIsExcelExporting(true);
    try {
      await exportAnalysisExcel({
        measurements: analysisData?.measurements || {},
        dosing: analysisData?.dosing || {},
        events: analysisData?.events || [],
        selectedParameters: selection.parameters,
        selectedDosing: selection.dosing,
        selectedEvents: selection.events,
        dateRange: { start: startDateString, end: endDateString },
        tankName: selectedTank?.name,
      });
      notifications.show({ title: 'Data exported', message: 'Analysis data saved as spreadsheet', color: 'green', autoClose: 3000 });
    } catch {
      notifications.show({ title: 'Export failed', message: 'Could not export data. Please try again.', color: 'red' });
    } finally {
      setIsExcelExporting(false);
    }
  }, [analysisData, selection, startDateString, endDateString, selectedTank?.name]);

  const handleStarClick = useCallback(() => {
    if (starState !== 'saved') {
      openSaveModal();
    }
  }, [openSaveModal, starState]);

  // Calculate total selected items for the empty state check
  const totalSelectedItems = useMemo(() => {
    return (
      selection.parameters.length +
      selection.dosing.length +
      selection.events.length
    );
  }, [selection]);

  // Show alert if no tank selected
  if (!selectedTank) {
    return (
      <Box p="xl">
        <NoTankSelected purpose="to view analysis" />
      </Box>
    );
  }

  const SidebarContent = (
    <Box>
      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : error ? (
        <Alert icon={<IconAlertCircle size={16} />} color="red" m="md">
          Failed to load available data
        </Alert>
      ) : (
        <AnalysisSidebar
          available={availableData}
          selected={selection}
          onChange={handleSelectionChange}
          savedViews={savedViews}
          activeViewId={activeView?.id || null}
          onLoadView={handleLoadView}
          onRenameView={handleRenameView}
          onDeleteView={handleDeleteView}
        />
      )}
    </Box>
  );

  return (
    <Box style={{ display: 'flex', height: '100%', minHeight: 'calc(100vh - 60px)' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Box
          style={{
            width: SIDEBAR_WIDTH,
            minWidth: SIDEBAR_WIDTH,
            borderRight: '1px solid var(--mantine-color-gray-3)',
            backgroundColor: 'var(--mantine-color-body)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              Data Series
            </Text>
          </Box>
          <Box style={{ flex: 1, overflowY: 'auto' }}>{SidebarContent}</Box>
        </Box>
      )}

      {/* Mobile Sidebar Drawer */}
      <Drawer opened={sidebarOpened} onClose={closeSidebar} title="Data Series" size={SIDEBAR_WIDTH} padding="md">
        {SidebarContent}
      </Drawer>

      {/* Main Content Area */}
      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: 'var(--mantine-color-gray-0)' }}>
        <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)', backgroundColor: 'var(--mantine-color-body)' }}>
          <Group justify="space-between" wrap="nowrap">
            <Group wrap="nowrap">
              {isMobile && (
                <ActionIcon variant="subtle" onClick={openSidebar} aria-label="Open data series sidebar">
                  <IconMenu2 size={20} />
                </ActionIcon>
              )}
              <div>
                <Title order={2}>Analysis</Title>
                {selectedTank && <Text size="sm" c="dimmed">{selectedTank.name}</Text>}
              </div>
            </Group>
            <Group gap="xs" wrap="nowrap">
              {/* Download menu */}
              <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                  <Tooltip label="Download chart data" withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      disabled={totalSelectedItems === 0}
                      loading={isExporting || isExcelExporting}
                      aria-label="Download chart data"
                    >
                      <IconDownload size={20} />
                    </ActionIcon>
                  </Tooltip>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconPhoto size={14} />}
                    onClick={handleExportImage}
                  >
                    Download as Image
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconFileSpreadsheet size={14} />}
                    onClick={handleExportExcel}
                  >
                    Download as Excel
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              {/* Save view star icon */}
              <Tooltip
                label={
                  starState === 'saved'
                    ? `Viewing "${activeView?.name}"`
                    : starState === 'modified'
                      ? `Modified from "${activeView?.name}" — click to save`
                      : 'Save this view'
                }
              >
                <ActionIcon
                  variant="subtle"
                  onClick={handleStarClick}
                  aria-label="Save analysis view"
                  style={{ position: 'relative' }}
                >
                  {starState === 'empty' ? (
                    <IconStar size={20} />
                  ) : (
                    <IconStarFilled size={20} color="var(--mantine-color-teal-6)" />
                  )}
                  {starState === 'modified' && (
                    <Box
                      style={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: 'var(--mantine-color-teal-6)',
                        border: '2px solid var(--mantine-color-body)',
                      }}
                    />
                  )}
                </ActionIcon>
              </Tooltip>
              {/* Desktop: inline Event lines toggle - only show when events are selected */}
              {!isMobile && selection.events.length > 0 && (
                <Tooltip label="Show vertical lines on charts at event dates">
                  <Group gap={4} wrap="nowrap">
                    <IconLine size={16} color={showEventLines ? 'var(--mantine-color-blue-6)' : 'var(--mantine-color-gray-5)'} />
                    <Switch
                      size="xs"
                      checked={showEventLines}
                      onChange={(event) => setShowEventLines(event.currentTarget.checked)}
                      aria-label="Show event lines on charts"
                    />
                    <Text size="xs" c={showEventLines ? 'blue' : 'dimmed'}>
                      Event Lines
                    </Text>
                  </Group>
                </Tooltip>
              )}
              <Tooltip label={viewMode === 'stacked' ? 'Stacked view: Individual charts' : 'Combined view: All parameters on one chart'}>
                <SegmentedControl
                  size="xs"
                  value={viewMode}
                  onChange={(value) => setViewMode(value as ViewMode)}
                  data={[
                    {
                      value: 'stacked',
                      label: (
                        <Group gap={4} wrap="nowrap">
                          <IconLayoutRows size={16} />
                          {!isMobile && <span>Stacked</span>}
                        </Group>
                      ),
                    },
                    {
                      value: 'combined',
                      label: (
                        <Group gap={4} wrap="nowrap">
                          <IconChartLine size={16} />
                          {!isMobile && <span>Combined</span>}
                        </Group>
                      ),
                    },
                  ]}
                  aria-label="Chart view mode"
                />
              </Tooltip>
              {/* Mobile: all display controls collapse into a single popover */}
              {isMobile && (
                <ChartOptionsPopover
                  viewMode={viewMode}
                  normalized={normalized}
                  onNormalizedChange={setNormalized}
                  smoothing={smoothing}
                  onSmoothingChange={setSmoothing}
                  hasEvents={selection.events.length > 0}
                  eventLines={showEventLines}
                  onEventLinesChange={setShowEventLines}
                />
              )}
              {/* Desktop: inline Smoothing Select - applies to both view modes */}
              {!isMobile && (
                <Tooltip label="Moving-average smoothing window">
                  <Select
                    size="xs"
                    w={120}
                    value={smoothing}
                    onChange={(v) => setSmoothing((v as SmoothingWindow) ?? 'off')}
                    data={SMOOTHING_OPTIONS}
                    allowDeselect={false}
                    comboboxProps={{ withinPortal: true }}
                    aria-label="Smoothing window"
                  />
                </Tooltip>
              )}
              {/* Desktop: inline Normalized toggle - combined view only */}
              {!isMobile && viewMode === 'combined' && (
                <Tooltip label={normalized ? 'Showing normalized % deviation from mean' : 'Showing raw values'}>
                  <Group gap={4} wrap="nowrap">
                    <Switch
                      size="xs"
                      checked={normalized}
                      onChange={(event) => setNormalized(event.currentTarget.checked)}
                      aria-label="Toggle normalized values"
                    />
                    <Text size="xs" c={normalized ? 'blue' : 'dimmed'}>
                      Normalized
                    </Text>
                  </Group>
                </Tooltip>
              )}
            </Group>
          </Group>

          {/* Row 2: Time controls */}
          <Group mt="sm" justify="space-between" wrap="wrap" gap="md">
            <AnalysisTimeControls dateRange={dateRange} onChange={setDateRange} />
          </Group>
        </Box>
        <Box ref={chartAreaRef} p="md" style={{ flex: 1, overflowY: 'auto' }}>
          {totalSelectedItems === 0 && <AnalysisEmptyState />}
          {totalSelectedItems > 0 && viewMode === 'stacked' && (
            <AnalysisErrorBoundary chartName="Stacked">
              <AnalysisStackedView
                selectedParameters={selection.parameters}
                measurements={analysisData?.measurements || {}}
                selectedDosing={selection.dosing}
                dosing={analysisData?.dosing || {}}
                selectedEvents={selection.events}
                events={analysisData?.events || []}
                dateRange={{ start: startDateString, end: endDateString }}
                showEventLines={showEventLines}
                smoothingWindowMs={smoothingWindowToMs(smoothing)}
                targetRanges={targetRangesData?.ranges}
                annotations={annotations}
                onAddNote={handleAddNote}
                onAnnotationClick={handleEditAnnotation}
              />
            </AnalysisErrorBoundary>
          )}
          {totalSelectedItems > 0 && viewMode === 'combined' && (
            <AnalysisErrorBoundary chartName="Combined">
              <AnalysisCombinedView
                selectedParameters={selection.parameters}
                measurements={analysisData?.measurements || {}}
                selectedDosing={selection.dosing}
                dosing={analysisData?.dosing || {}}
                selectedEvents={selection.events}
                events={analysisData?.events || []}
                dateRange={{ start: startDateString, end: endDateString }}
                showEventLines={showEventLines}
                normalized={normalized}
                smoothingWindowMs={smoothingWindowToMs(smoothing)}
                targetRanges={targetRangesData?.ranges}
                annotations={annotations}
                onAddNote={handleAddNote}
                onAnnotationClick={handleEditAnnotation}
              />
            </AnalysisErrorBoundary>
          )}
        </Box>
      </Box>

      {/* Save View Modal */}
      <SaveViewModal
        opened={saveModalOpened}
        onClose={closeSaveModal}
        config={currentConfig}
        activeView={isModified ? activeView : null}
        onSaveNew={handleSaveNew}
        onUpdate={handleUpdateView}
      />

      <AddAnnotationModal
        opened={annotationModalOpened}
        onClose={() => {
          setAnnotationModalOpened(false);
          setSelectedAnnotation(null);
        }}
        tankId={selectedTank?.id}
        chartContext={annotationChartContext || { parameter: '', parameterLabel: '' }}
        existingAnnotation={selectedAnnotation}
      />
    </Box>
  );
}
